import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import type { Goal } from '../../types/goal'

interface GoalFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialDate?: string
  goal?: Goal | null
  onSave: (data: { body: string; target_date: string }) => Promise<void>
}

export function GoalFormDialog({ open, onOpenChange, initialDate, goal, onSave }: GoalFormDialogProps) {
  const { t } = useTranslation()
  const [body, setBody] = useState('')
  const [targetDate, setTargetDate] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setBody(goal?.body ?? '')
      setTargetDate(goal?.target_date ?? initialDate ?? '')
    }
  }, [open, goal, initialDate])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!body.trim() || !targetDate) return
    setSaving(true)
    try {
      await onSave({ body: body.trim(), target_date: targetDate })
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }

  const isEdit = !!goal
  const title = isEdit
    ? t('trainingDiary.dialog.goal.title.edit')
    : t('trainingDiary.dialog.goal.title.create')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="goal-body">{t('trainingDiary.goals.title')}</Label>
            <Textarea
              id="goal-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={200}
              rows={3}
              required
            />
            <span className="text-xs text-muted-foreground text-right">{body.length}/200</span>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="goal-date">{t('trainingDiary.goals.targetDate', 'Target date')}</Label>
            <Input
              id="goal-date"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              required
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
